import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft } from 'lucide-react';
import { DineInThemeShell } from '../components/DineInThemeShell';
import { HallCheckPanel } from '../components/HallCheckPanel';
import { useDineInMenuTheme } from '../hooks/useDineInMenuTheme';
import { hallApi, type HallTable } from '../services/hallApi';
import { dineInMenuQueryOptions } from '../queries/menu';
import {
  buildCaseHallTable,
  isHallCaseMockOrder,
  isHallCaseTable,
} from '../data/hallCaseMock';

export default function TipOrderScreen() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { theme, tokens, fadeMs } = useDineInMenuTheme();
  const caseMock = isHallCaseTable(searchParams) || isHallCaseMockOrder(orderId);
  const { data: categories = [] } = useQuery({ ...dineInMenuQueryOptions(), enabled: caseMock });
  const caseTable = useMemo(
    () => (caseMock ? buildCaseHallTable(categories, { billed: true }) : null),
    [caseMock, categories],
  );
  const [data, setData] = useState<HallTable | null>(null);
  const [error, setError] = useState<string | null>(null);
  const shown = caseMock ? caseTable : data;

  useEffect(() => {
    if (caseMock || !orderId) return;
    hallApi
      .getOrder(orderId)
      .then(setData)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Не удалось загрузить заказ'));
  }, [orderId, caseMock]);

  return (
    <DineInThemeShell theme={theme} fadeMs={fadeMs}>
      <div className="max-w-md mx-auto w-full px-4 pt-5 pb-16" style={{ color: tokens.text }}>
        <Link
          to={caseMock ? '/menu?case=table20' : '/menu'}
          className="flex items-center gap-0.5 text-[12px] tracking-[0.14em] uppercase mb-8"
          style={{ color: tokens.muted, fontFamily: 'Manrope, sans-serif' }}
        >
          <ChevronLeft size={16} /> Меню
        </Link>
        <h1
          className="text-[2rem] leading-none font-bold uppercase mb-6"
          style={{ fontFamily: 'Unbounded, sans-serif', letterSpacing: '-0.04em' }}
        >
          Чаевые
        </h1>
        {error ? <p className="text-sm text-red-500">{error}</p> : null}
        {shown ? (
          <HallCheckPanel
            data={shown}
            tokens={tokens}
            payWith={caseMock ? {} : { orderId: shown.order?.id, tableId: shown.tableId }}
            mockOnly={caseMock}
            showTips
            onMockPay={() => navigate('/hall-payment-return?case=tipped')}
          />
        ) : !error ? (
          <p className="text-sm" style={{ color: tokens.muted }}>
            Загрузка…
          </p>
        ) : null}
      </div>
    </DineInThemeShell>
  );
}
